import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { customBaseUrl, httpGet, httpPost, httpGetPdf, httpGetBlob, httpPatch } from '../../store/http';
import { saveAs } from 'file-saver';
import { NotificationManager } from 'react-notifications';
import {
  Card, Button, Space, Modal, Input, Form, DatePicker, Checkbox, Table, Typography, Row, Col, Tag, Divider, Avatar, Descriptions, Select
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, FileTextOutlined, BankOutlined, HistoryOutlined, SafetyCertificateOutlined, UserOutlined, DollarOutlined, SearchOutlined
} from '@ant-design/icons';
import LoanDashboardLayout from '../../components/Layout/LoanDashboardLayout';
import LoanHistoryModal from '../../components/Modal/LoanHistoryModal';
import ApprovalHistoryModal from '../../components/Modal/ApprovalHistoryModal';
import moment from 'moment';
import { hideLoader, showLoader } from '../../utils/loader';
import AdminLayout from "../../components/Layout";
import { CardWrapper, useMatStyles } from './style';
import slugs from '../../resources/slugs';
import { useNavigate } from 'react-router-dom';
import { Alert } from 'antd';

const { Title, Text } = Typography;
const { TextArea } = Input;

const FinanceOfficerDetailsPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const classes = useMatStyles();
  const { from } = location.state || {};
  const [loanDetails, setLoanDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isOtherDataModalVisible, setIsOtherDataModalVisible] = useState(false);
  const [otherDataContent, setOtherDataContent] = useState({});
  const [isMandateModalVisible, setIsMandateModalVisible] = useState(false);
  const [mandateDetails, setMandateDetails] = useState(null);
  const [isMandateBalanceModalVisible, setIsMandateBalanceModalVisible] = useState(false);
  const [mandateBalance, setMandateBalance] = useState(null);
  const [mandateBalanceAmount, setMandateBalanceAmount] = useState(0);
  const [isLoanHistoryModalVisible, setIsLoanHistoryModalVisible] = useState(false);
  const [selectedLoanIdForHistory, setSelectedLoanIdForHistory] = useState(null);

  const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
  const [financeApprovalNotes, setFinanceApprovalNotes] = useState('');
  const [loanStartDate, setLoanStartDate] = useState(null);
  const [approvalHistory, setApprovalHistory] = useState(null);
  const [isApprovalHistoryModalVisible, setIsApprovalHistoryModalVisible] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [isGuarantorDetailsModalVisible, setIsGuarantorDetailsModalVisible] = useState(false);
  const [selectedGuarantorDetails, setSelectedGuarantorDetails] = useState(null);
  const [isVerifyOtpModalVisible, setIsVerifyOtpModalVisible] = useState(false);
  const [currentGuarantorAuthUserId, setCurrentGuarantorAuthUserId] = useState(null);
  const [otpInput, setOtpInput] = useState('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);

  const [isUpdateCycleModalVisible, setIsUpdateCycleModalVisible] = useState(false);
  const [updatingCycle, setUpdatingCycle] = useState(false);
  const [repaymentCycleValue, setRepaymentCycleValue] = useState(30);
  const [repaymentCycleUnit, setRepaymentCycleUnit] = useState('days');
  const [firstRepaymentDelayValue, setFirstRepaymentDelayValue] = useState('');
  const [firstRepaymentDelayUnit, setFirstRepaymentDelayUnit] = useState(undefined);

  useEffect(() => {
    const fetchLoanDetails = async () => {
      try {
        const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
        showLoader();
        const res = await httpGet(
          `${customBaseUrl.loanUrl}/api/v1/admin/loans/${id}`,
          null,
          false,
          false,
          token
        );
        if (res.status) {
          setLoanDetails(res.data);
        } else {
          NotificationManager.error(res.message || 'Failed to fetch loan details');
        }
      } catch (error) {
        console.error('Error fetching loan details:', error);
        NotificationManager.error('Error fetching loan details');
      } finally {
        setLoading(false);
        hideLoader();
      }
    };

    fetchLoanDetails();
  }, [id, from, refresh]);

  const handleApproveSubmit = async () => {
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const payload = {
        action: "Approve",
        notes: financeApprovalNotes,
        loan_start_date: loanStartDate ? moment(loanStartDate).format('MM-DD-YYYY') : null,
      };
      showLoader();
      const res = await httpPost(
        `${customBaseUrl.loanUrl}/api/v1/loans/${id}/finance-approval`,
        payload,
        false,
        token
      );

      if (res.status) {
        NotificationManager.success('Loan approved successfully by Finance Officer');
        setIsApproveModalVisible(false);
        setRefresh(!refresh);
      } else {
        NotificationManager.error(res.message || 'Failed to approve loan');
      }
    } catch (error) {
      NotificationManager.error('Error approving loan');
    } finally {
      hideLoader();
    }
  };

  const handleRejectSubmit = async () => {
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const payload = {
        action: "Reject",
        notes: rejectionReason,
      };
      showLoader();
      const res = await httpPost(
        `${customBaseUrl.loanUrl}/api/v1/loans/${id}/finance-approval`,
        payload,
        false,
        token
      );

      if (res.status) {
        NotificationManager.success('Loan rejected successfully by Finance Officer');
        setIsRejectModalVisible(false);
        setRejectionReason('');
        setRefresh(!refresh);
      } else {
        NotificationManager.error(res.message || 'Failed to reject loan');
      }
    } catch (error) {
      NotificationManager.error('Error rejecting loan');
    } finally {
      hideLoader();
    }
  };

  const handleUpdateRepaymentCycle = async () => {
    if (!repaymentCycleValue || repaymentCycleValue < 1) {
      NotificationManager.error('Repayment cycle value must be a positive integer');
      return;
    }
    if (!repaymentCycleUnit) {
      NotificationManager.error('Repayment cycle unit is required');
      return;
    }

    setUpdatingCycle(true);
    showLoader();
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const payload = {
        repayment_cycle_value: Number(repaymentCycleValue),
        repayment_cycle_unit: repaymentCycleUnit,
      };
      if (firstRepaymentDelayValue !== '') {
        payload.first_repayment_delay_value = Number(firstRepaymentDelayValue);
      }
      if (firstRepaymentDelayUnit) {
        payload.first_repayment_delay_unit = firstRepaymentDelayUnit;
      }

      const res = await httpPatch(
        `${customBaseUrl.loanUrl}/api/v1/admin/loans/${id}/repayment-cycle`,
        payload,
        null,
        false,
        token
      );

      if (res.status) {
        NotificationManager.success(res.message || 'Repayment cycle updated successfully');
        setIsUpdateCycleModalVisible(false);
        setRefresh(!refresh);
      } else {
        NotificationManager.error(res.message || 'Failed to update repayment cycle');
      }
    } catch (error) {
      NotificationManager.error(error.message || 'An error occurred during repayment cycle update');
    } finally {
      setUpdatingCycle(false);
      hideLoader();
    }
  };

  const handleViewMandate = async () => {
    showLoader();
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpGet(
        `${customBaseUrl.loanUrl}/api/v1/admin/mandates/${loanDetails.mandate_id}`,
        null,
        false,
        false,
        token
      );
      if (res.status) {
        setMandateDetails(res.data);
        setIsMandateModalVisible(true);
      }
    } finally {
      hideLoader();
    }
  };

  const handleCheckMandateBalance = async () => {
    showLoader();
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpGet(
        `${customBaseUrl.loanUrl}/api/v1/admin/mandates/${loanDetails.mandate_id}/balance?amount=${mandateBalanceAmount}`,
        null,
        false,
        false,
        token
      );
      if (res.status) {
        setMandateBalance(res.data);
      }
    } finally {
      hideLoader();
    }
  };

  const handlePreviewOfferLetter = async () => {
    try {
      showLoader();
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpGetPdf(
        `${customBaseUrl.loanUrl}/api/v1/loans/${id}/preview-offer-letter`,
        null,
        false,
        false,
        token
      );

      const blob = new Blob([res], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `loan-offer-preview-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      NotificationManager.error('Error fetching offer letter preview');
    } finally {
      hideLoader();
    }
  };

  const handleViewApprovalHistory = async () => {
    showLoader();
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpGet(
        `${customBaseUrl.loanUrl}/api/v1/loans/${id}/history`,
        null,
        false,
        false,
        token
      );
      if (res) {
        setApprovalHistory(res);
        setIsApprovalHistoryModalVisible(true);
      }
    } finally {
      hideLoader();
    }
  };

  const handleExportDetails = async () => {
    try {
      showLoader();
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpGetBlob(
        `${customBaseUrl.loanUrl}/api/v1/admin/actions/${id}/export-details`,
        token
      );
      const timestamp = moment().format('YYYYMMDD_HHmmss');
      saveAs(res, `Loan_Participant_Details_${id}_${timestamp}.xlsx`);
      NotificationManager.success('Profile details exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      NotificationManager.error('Failed to export profile details');
    } finally {
      hideLoader();
    }
  };

  const handleResendGuarantorOtp = async (guarantorAuthUserId) => {
    setResendingOtp(true);
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpPost(
        `${customBaseUrl.loanUrl}/api/v1/loans/${id}/resend-guarantor-otp`,
        { guarantor_auth_user_id: guarantorAuthUserId },
        false,
        token
      );
      if (res.status) {
        NotificationManager.success(res.message || "OTP resent successfully!");
      } else {
        NotificationManager.error(res.message || "Failed to resend OTP.");
      }
    } catch (error) {
      NotificationManager.error("Error resending OTP.");
    } finally {
      setResendingOtp(false);
    }
  };

  const handleVerifyGuarantorOtp = async () => {
    if (!otpInput) {
      NotificationManager.warning("Please enter the OTP.");
      return;
    }
    setVerifyingOtp(true);
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpPost(
        `${customBaseUrl.loanUrl}/api/v1/loans/${id}/verify-otp`,
        {
          otp: otpInput,
          guarantor_auth_user_id: currentGuarantorAuthUserId
        },
        false,
        token
      );
      if (res.status) {
        NotificationManager.success(res.message || "Guarantor OTP verified successfully!");
        setIsVerifyOtpModalVisible(false);
        setOtpInput('');
        setRefresh(prev => !prev);
      } else {
        NotificationManager.error(res.message || "Invalid OTP.");
      }
    } catch (error) {
      NotificationManager.error("Error verifying OTP.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleVerifyGuarantor = async (accountNumber) => {
    if (!accountNumber) {
      NotificationManager.warning("No account number found for this guarantor.");
      return;
    }
    showLoader();
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpGet(
        `${customBaseUrl.loanUrl}/api/v1/admin/users/account/${accountNumber}`,
        null,
        false,
        false,
        token
      );
      if (res.status && res.data) {
        setSelectedGuarantorDetails(res.data);
        setIsGuarantorDetailsModalVisible(true);
        NotificationManager.success("Guarantor account details fetched successfully!");
      } else {
        NotificationManager.error(res.message || "Failed to fetch guarantor details.");
      }
    } catch (error) {
      NotificationManager.error("Error fetching guarantor details.");
    } finally {
      hideLoader();
    }
  };

  const Layout = from === "admin" ? AdminLayout : LoanDashboardLayout;

  if (loading || !loanDetails) {
    return <Layout>{loading ? 'Loading...' : 'Loan not found.'}</Layout>;
  }

  const InfoRow = ({ label, value, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
      <Text type="secondary" style={{ fontSize: 13 }}>{label}</Text>
      <Text strong style={{ color: color || '#1a1a1a' }}>{value}</Text>
    </div>
  );

  return (
    <Layout>
      <div style={{ padding: '0 32px 32px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div>
            <Title level={2} style={{ margin: 0, fontWeight: 800 }}>Loan Detail (Finance View)</Title>
            <Space>
              <Tag color="blue" style={{ borderRadius: 6 }}>ID: {loanDetails.id}</Tag>
              <Tag color={loanDetails.status === 'Approved' ? 'green' : 'orange'} style={{ borderRadius: 6 }}>{loanDetails.status}</Tag>
            </Space>
          </div>
          <Space>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              style={{ height: 44, borderRadius: 10, background: '#27AE60', border: 'none' }}
              onClick={() => setIsApproveModalVisible(true)}
            >
              Finance Approve
            </Button>
            <Button
              danger
              icon={<CloseCircleOutlined />}
              style={{ height: 44, borderRadius: 10 }}
              onClick={() => setIsRejectModalVisible(true)}
            >
              Reject
            </Button>
            {loanDetails.loan_admin_approval_status === "Approved" &&
              loanDetails.loan_officer_approval_status === "Approved" && (
                <Button
                  icon={<FileTextOutlined />}
                  style={{ height: 44, borderRadius: 10, background: '#0086E8', color: '#fff', border: 'none' }}
                  onClick={handlePreviewOfferLetter}
                >
                  Preview Offer Letter
                </Button>
              )}
          </Space>
        </div>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
              <CardWrapper>
                <Card style={{ borderRadius: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24 }}>
                    <Avatar size={64} icon={<UserOutlined />} style={{ backgroundColor: '#FF4B01' }} />
                    <div>
                      <Title level={4} style={{ margin: 0 }}>{loanDetails.user?.first_name} {loanDetails.user?.last_name}</Title>
                      <Text type="secondary">{loanDetails.user?.email} • {loanDetails.user?.phone}</Text>
                    </div>
                  </div>
                  <Divider style={{ margin: '16px 0' }} />
                  <Row gutter={40}>
                    <Col span={12}>
                      <InfoRow label="KYC Level" value={loanDetails.user?.kyc_level} />
                      <InfoRow label="KYC Status" value={loanDetails.user?.other_data?.kycStatus} />
                      <InfoRow label="BVN" value={loanDetails.user?.bvn} />
                    </Col>
                    <Col span={12}>
                      <InfoRow label="User Type" value={loanDetails.user?.other_data?.userType} />
                      <InfoRow label="Account Status" value={loanDetails.user?.other_data?.status} />
                      <Button type="link" style={{ padding: 0 }} onClick={() => {
                        setOtherDataContent(loanDetails.user?.other_data || {});
                        setIsOtherDataModalVisible(true);
                      }}>View Full Profile</Button>
                    </Col>
                  </Row>
                </Card>
              </CardWrapper>

               {loanDetails.installment_dates?.length > 0 && (
                <Card title={<Space><DollarOutlined /> Payment Schedule</Space>} style={{ borderRadius: 16 }}>
                  <Table
                    dataSource={loanDetails.installment_dates.map((item, index) => ({
                      key: index,
                      date: moment(item.date).format('YYYY-MM-DD HH:mm:ss'),
                      amount: `₦${Number(item.amount).toLocaleString()}`,
                    }))}
                    columns={[
                      { title: 'Payment Date', dataIndex: 'date', key: 'date' },
                      { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right' },
                    ]}
                    pagination={false}
                    size="small"
                  />
                  {(loanDetails.status === 'Disbursed' || loanDetails.status === 'Completed') && (
                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                      <Button
                        type="primary"
                        onClick={() => {
                          setRepaymentCycleValue(loanDetails.repayment_cycle_value || 30);
                          setRepaymentCycleUnit(loanDetails.repayment_cycle_unit || 'days');
                          setFirstRepaymentDelayValue(loanDetails.first_repayment_delay_value || '');
                          setFirstRepaymentDelayUnit(loanDetails.first_repayment_delay_unit || undefined);
                          setIsUpdateCycleModalVisible(true);
                        }}
                      >
                        Update Repayment Cycle
                      </Button>
                    </div>
                  )}
                </Card>
              )}

              <Row gutter={24}>
                <Col span={12}>
                  <Card title={<Space><FileTextOutlined /> Employment</Space>} style={{ borderRadius: 16, height: '100%' }}>
                    <InfoRow label="Employer" value={loanDetails.employment_details?.employer_name || loanDetails.employment_details?.employer} />
                    <InfoRow label="Salary" value={`₦${loanDetails.employment_details?.salary?.toLocaleString()}`} />
                    <Divider style={{ margin: '12px 0' }} />
                    <InfoRow label="HR Name" value={loanDetails.employment_details?.hr_name || 'N/A'} />
                    <InfoRow label="HR Email" value={loanDetails.employment_details?.hr_email || 'N/A'} />
                    <InfoRow label="HR Phone" value={loanDetails.employment_details?.hr_phone || 'N/A'} />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title={<Space><BankOutlined /> Bank Information</Space>} style={{ borderRadius: 16, height: '100%' }}>
                    {loanDetails.account_details?.map((acc, i) => (
                      <div key={i} style={{ marginBottom: i === 0 ? 12 : 0 }}>
                        <InfoRow label="Bank" value={acc?.institution?.name} />
                        <InfoRow label="A/C No" value={acc?.account_number} />
                      </div>
                    ))}
                  </Card>
                </Col>
              </Row>
            </Space>
          </Col>

          <Col xs={24} lg={8}>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
              <Card title="Financial Summary" style={{ borderRadius: 16 }}>
                <div style={{ textAlign: 'center', marginBottom: 24, padding: '16px', background: '#f8f9fa', borderRadius: 12 }}>
                  <Text type="secondary" block>Total Amount</Text>
                  <Title level={2} style={{ margin: 0, color: '#FF4B01' }}>₦{loanDetails.amount?.toLocaleString()}</Title>
                </div>
                <InfoRow label="Term" value={`${loanDetails.term_months} Months`} color="#0086E8" />
                <InfoRow label="Repayment Account" value={loanDetails.waya_repayment_account || 'N/A'} />
              </Card>

              <Card title="Finance Actions" style={{ borderRadius: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  {loanDetails.loan_admin_approval_status === "Approved" &&
                    loanDetails.loan_officer_approval_status === "Approved" &&
                    loanDetails.finance_approver_approval_status === "Approved" && (
                      <Button block icon={<FileTextOutlined />} onClick={handlePreviewOfferLetter}>Preview Offer Letter</Button>
                    )}
                  <Button block icon={<HistoryOutlined />} onClick={handleViewApprovalHistory}>View Approval History</Button>
                  <Button block icon={<SafetyCertificateOutlined />} onClick={handleViewMandate}>View Mandate Details</Button>
                  <Button block icon={<DollarOutlined />} onClick={() => setIsMandateBalanceModalVisible(true)}>Check Mandate Balance</Button>
                   <Button block icon={<FileTextOutlined />} onClick={handleExportDetails}>Download Profile Details (Excel)</Button>
                  {(loanDetails.offer_letter_document || loanDetails.offer_url) && (
                    <Button
                      block
                      type="primary"
                      style={{ background: '#52c41a', borderColor: '#52c41a' }}
                      icon={<FileTextOutlined />}
                      onClick={() => window.open(loanDetails.offer_letter_document || loanDetails.offer_url, '_blank')}
                    >
                      View Signed Offer Letter
                    </Button>
                  )}
                  {loanDetails.files?.account_statement_url && (
                    <Button block type="dashed" href={loanDetails.files.account_statement_url} target="_blank">
                      View Bank Statement
                    </Button>
                  )}
                </Space>
              </Card>

              <Card title="Verification Documents" style={{ borderRadius: 16 }}>
                {loanDetails.verification_documents && Object.values(loanDetails.verification_documents).some(v => v) ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {Object.entries(loanDetails.verification_documents)
                      .filter(([key, value]) => value)
                      .map(([key, value]) => (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <Text style={{ fontSize: 13 }}>{key.replace(/_/g, ' ')}</Text>
                          <Button
                            type="link"
                            size="small"
                            icon={<FileTextOutlined />}
                            href={value}
                            target="_blank"
                          >
                            View
                          </Button>
                        </div>
                      ))}
                  </Space>
                ) : (
                  <Text type="secondary" italic>No verification documents uploaded yet.</Text>
                )}
              </Card>

              <Card title="Previous Approvals" style={{ borderRadius: 16 }}>
                <InfoRow label="Admin Review" value={<Tag color={loanDetails.loan_admin_approval_status === 'Approved' ? 'success' : 'default'}>{loanDetails.loan_admin_approval_status || 'Pending'}</Tag>} />
                <InfoRow label="Officer Review" value={<Tag color={loanDetails.loan_officer_approval_status === 'Approved' ? 'success' : 'default'}>{loanDetails.loan_officer_approval_status || 'Pending'}</Tag>} />
              </Card>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Modals */}
      <Modal
        title="Finance Approval"
        visible={isApproveModalVisible}
        onOk={handleApproveSubmit}
        onCancel={() => setIsApproveModalVisible(false)}
        okText="Confirm & Approve"
        width={500}
      >
        <Form layout="vertical">
          <Form.Item label="Notes">
            <TextArea
              rows={4}
              value={financeApprovalNotes}
              onChange={(e) => setFinanceApprovalNotes(e.target.value)}
              placeholder="Enter optional notes for this approval..."
            />
          </Form.Item>
          <Form.Item label="Set Loan Start Date">
            <DatePicker
              style={{ width: '100%' }}
              value={loanStartDate ? moment(loanStartDate) : null}
              onChange={(date, dateString) => setLoanStartDate(dateString)}
              format="YYYY-MM-DD"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Reject Loan (Finance)"
        visible={isRejectModalVisible}
        onOk={handleRejectSubmit}
        onCancel={() => setIsRejectModalVisible(false)}
        okText="Confirm Rejection"
        okButtonProps={{ danger: true }}
      >
        <Form layout="vertical">
          <Form.Item label="Reason for Rejection" required>
            <TextArea rows={4} value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} placeholder="Please provide specific reasons..." />
          </Form.Item>
        </Form>
      </Modal>

      <LoanHistoryModal showModal={isLoanHistoryModalVisible} hideModal={() => setIsLoanHistoryModalVisible(false)} loanId={id} />

      <ApprovalHistoryModal
        visible={isApprovalHistoryModalVisible}
        onCancel={() => setIsApprovalHistoryModalVisible(false)}
        historyData={approvalHistory}
      />

      <Modal title="Mandate Details" visible={isMandateModalVisible} onCancel={() => setIsMandateModalVisible(false)} footer={null} width={800}>
        {mandateDetails && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions bordered column={2} title="General Information">
              <Descriptions.Item label="Mandate ID">{mandateDetails.id}</Descriptions.Item>
              <Descriptions.Item label="Reference">{mandateDetails.reference}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={mandateDetails.status === 'approved' ? 'green' : 'orange'}>
                  {mandateDetails.status?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Verification Method">{mandateDetails.verification_method?.replace(/_/g, ' ')}</Descriptions.Item>
              <Descriptions.Item label="Amount">₦{(mandateDetails.amount / 100).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Balance">₦{(mandateDetails.balance / 100).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Start Date">{moment(mandateDetails.start_date).format('LL')}</Descriptions.Item>
              <Descriptions.Item label="End Date">{moment(mandateDetails.end_date).format('LL')}</Descriptions.Item>
            </Descriptions>

            <Descriptions bordered column={2} title="Account Details">
              <Descriptions.Item label="Account Name">{mandateDetails.account_name}</Descriptions.Item>
              <Descriptions.Item label="Account Number">{mandateDetails.account_number}</Descriptions.Item>
              <Descriptions.Item label="Bank">{mandateDetails.institution?.name}</Descriptions.Item>
              <Descriptions.Item label="Bank Code">{mandateDetails.institution?.bank_code}</Descriptions.Item>
              <Descriptions.Item label="NIP Code">{mandateDetails.institution?.nip_code}</Descriptions.Item>
              <Descriptions.Item label="NIBSS Code">{mandateDetails.nibss_code}</Descriptions.Item>
            </Descriptions>

            <Descriptions bordered column={2} title="Configuration & Status">
              <Descriptions.Item label="Mandate Type">{mandateDetails.mandate_type?.toUpperCase()}</Descriptions.Item>
              <Descriptions.Item label="Debit Type">{mandateDetails.debit_type?.toUpperCase()}</Descriptions.Item>
              <Descriptions.Item label="Approved">
                {mandateDetails.approved ? <Tag color="success">YES</Tag> : <Tag color="error">NO</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Ready to Debit">
                {mandateDetails.ready_to_debit ? <Tag color="success">YES</Tag> : <Tag color="error">NO</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Live Mode">
                {mandateDetails.live_mode ? <Tag color="blue">LIVE</Tag> : <Tag color="default">TEST</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Created Date">{moment(mandateDetails.date).format('LLL')}</Descriptions.Item>
              <Descriptions.Item label="Narration" span={2}>{mandateDetails.narration}</Descriptions.Item>
            </Descriptions>

            {mandateDetails.linked_mandates?.length > 0 && (
              <Card title="Linked Mandates" size="small" style={{ borderRadius: 12 }}>
                <Table
                  dataSource={mandateDetails.linked_mandates}
                  pagination={false}
                  size="small"
                  columns={[
                    { title: 'Account Name', dataIndex: 'account_name', key: 'account_name' },
                    { title: 'Account Number', dataIndex: 'account_number', key: 'account_number' },
                    { title: 'Bank', dataIndex: 'bank_name', key: 'bank_name' },
                    {
                      title: 'Ready',
                      dataIndex: 'is_ready_to_debit',
                      key: 'is_ready_to_debit',
                      render: (ready) => ready ? <Tag color="success">YES</Tag> : <Tag color="error">NO</Tag>
                    },
                  ]}
                />
              </Card>
            )}
          </Space>
        )}
      </Modal>

      <Modal
        title="Check Mandate Balance"
        visible={isMandateBalanceModalVisible}
        onCancel={() => setIsMandateBalanceModalVisible(false)}
        footer={null}
      >
        <Form layout="vertical">
          <Form.Item label="Verification Amount">
            <Input
              type="number"
              value={mandateBalanceAmount}
              onChange={(e) => setMandateBalanceAmount(e.target.value)}
              placeholder="Enter amount"
            />
          </Form.Item>
          <Button type="primary" block onClick={handleCheckMandateBalance} style={{ background: '#FF4B01', border: 'none' }}>Verify Balance</Button>
        </Form>
        {mandateBalance && (
          <div style={{ marginTop: 24, padding: 16, background: '#f8f9fa', borderRadius: 8 }}>
            <InfoRow label="Sufficient Balance" value={mandateBalance.has_sufficient_balance ? <Tag color="success">YES</Tag> : <Tag color="error">NO</Tag>} />
          </div>
        )}
      </Modal>

      {/* Profile Detail Modal */}
      <Modal
        title="User Profile Details"
        visible={isOtherDataModalVisible}
        onCancel={() => setIsOtherDataModalVisible(false)}
        footer={null}
        width={800}
      >
        <Descriptions bordered column={2}>
          {Object.entries(otherDataContent)
            .filter(([key, val]) => typeof val !== 'object')
            .map(([key, value]) => (
              <Descriptions.Item label={key} key={key}>{String(value)}</Descriptions.Item>
            ))
          }
        </Descriptions>
      </Modal>

      <Modal
        title="Update Loan Repayment Cycle"
        visible={isUpdateCycleModalVisible}
        onOk={handleUpdateRepaymentCycle}
        onCancel={() => setIsUpdateCycleModalVisible(false)}
        confirmLoading={updatingCycle}
        okText="Update Cycle"
        width={500}
      >
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Repayment Cycle Value" required>
                <Input
                  type="number"
                  min={1}
                  value={repaymentCycleValue}
                  onChange={(e) => setRepaymentCycleValue(Number(e.target.value))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Repayment Cycle Unit" required>
                <Select
                  value={repaymentCycleUnit}
                  onChange={(val) => setRepaymentCycleUnit(val)}
                  options={[
                    { value: 'hours', label: 'Hours' },
                    { value: 'days', label: 'Days' },
                    { value: 'weeks', label: 'Weeks' },
                    { value: 'months', label: 'Months' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="First Repayment Delay Value">
                <Input
                  type="number"
                  min={1}
                  value={firstRepaymentDelayValue}
                  onChange={(e) => setFirstRepaymentDelayValue(e.target.value ? Number(e.target.value) : '')}
                  placeholder="Optional"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="First Repayment Delay Unit">
                <Select
                  value={firstRepaymentDelayUnit}
                  onChange={(val) => setFirstRepaymentDelayUnit(val)}
                  placeholder="Optional"
                  allowClear
                  options={[
                    { value: 'hours', label: 'Hours' },
                    { value: 'days', label: 'Days' },
                    { value: 'weeks', label: 'Weeks' },
                    { value: 'months', label: 'Months' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Layout>
  );
};

export default FinanceOfficerDetailsPage;